"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Linkset = void 0;
;
/**
 * A set of links.
 * @internal
 */
class Linkset {
    constructor(links) {
        this.elements = links;
        this.size = links.length;
    }
    /**
     * {@inheritDoc LinksetInterface.hasLinkTo}
     */
    hasLinkTo(relationType) {
        return this.elements.some((link) => link.rel === relationType);
    }
    /**
     * {@inheritDoc LinksetInterface.linkTo}
     */
    linkTo(relationType) {
        return this.elements.find((link) => link.rel === relationType);
    }
    /**
     * {@inheritDoc LinksetInterface.linksTo}
     */
    linksTo(relationType) {
        return new Linkset(this.elements.filter((link) => link.rel === relationType));
    }
    /**
     * {@inheritDoc LinksetInterface.linksFrom}
     */
    linksFrom(anchor) {
        return new Linkset(this.elements.filter((link) => link.anchor === anchor));
    }
    /**
     * {@inheritdoc LinksetInterface.linksWithAttribute}
     */
    linksWithAttribute(name) {
        return new Linkset(this.elements.filter((link) => Object.prototype.hasOwnProperty.call(link.attributes, name)));
    }
    /**
     * {@inheritdoc LinksetInterface.linksWithAttributeValue}
     */
    linksWithAttributeValue(name, value) {
        return new Linkset(this.linksWithAttribute(name).elements.filter((link) => {
            const values = (Array.isArray(link.attributes[name]) ? link.attributes[name] : [link.attributes[name]]);
            return values.some((v) => isAttributeEqual(v, value));
        }));
    }
    /**
     * Implements the iterable protocol.
     */
    [Symbol.iterator]() {
        const elems = this.elements;
        let pointer = 0;
        return {
            next() {
                if (pointer < elems.length) {
                    return { value: elems[pointer++], done: false };
                }
                else {
                    return { value: undefined, done: true };
                }
            }
        };
    }
    /**
     * {@inheritDoc NormalizableLinksetInterface.normalize}
     */
    normalize() {
        const contexts = {};
        this.elements.forEach((_a) => {
            var { anchor, rel } = _a, target = __rest(_a, ["anchor", "rel"]);
            if (!Object.hasOwnProperty.call(contexts, anchor))
                contexts[anchor] = {};
            if (!Object.hasOwnProperty.call(contexts[anchor], rel))
                contexts[anchor][rel] = [];
            const { href, attributes } = target;
            const targetObject = Object.assign({ href }, attributes);
            contexts[anchor][rel].push(targetObject);
        });
        return {
            linkset: Object.entries(contexts).reduce((carry, [anchor, rels]) => {
                return [...carry, Object.assign({ anchor }, rels)];
            }, []),
        };
    }
}
exports.Linkset = Linkset;
/**
 * Checks target attribute values for equality, respecting that a value may be internationalized.
 * @param a - A target attribute value.
 * @param b - Another target attribute value.
 * @returns boolean
 *   True if both values are strictly equivalent. If the input values are internationalized, both the value and language
 *   must match.
 * {@see {@link TargetAttributeValue}}
 */
function isAttributeEqual(a, b) {
    if (typeof a !== typeof b) {
        return false;
    }
    if (typeof a === 'string' && typeof b === 'string') {
        return a === b;
    }
    return a.value === a.value
        && a.language === a.language;
}
